import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/site";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("ce_admin_token") || "");
  const [password, setPassword] = useState("");
  const [tab, setTab] = useState("waitlist");
  const [waitlist, setWaitlist] = useState([]);
  const [partners, setPartners] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);

  const authed = !!token;

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/admin/login", { password });
      setToken(data.token);
      localStorage.setItem("ce_admin_token", data.token);
      toast.success("Welcome, Curator.");
    } catch (err) {
      toast.error("Access denied", { description: "Invalid password." });
    } finally { setLoading(false); }
  };

  const logout = () => {
    localStorage.removeItem("ce_admin_token");
    setToken(""); setWaitlist([]); setPartners([]); setContacts([]);
  };

  const fetchAll = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const headers = { "X-Admin-Token": token };
      const [w, p, c, a] = await Promise.all([
        api.get("/admin/waitlist", { headers }),
        api.get("/admin/partners", { headers }),
        api.get("/admin/contacts", { headers }),
        api.get("/admin/analytics", { headers }),
      ]);
      setWaitlist(w.data.entries || []);
      setPartners(p.data.entries || []);
      setContacts(c.data.entries || []);
      setAnalytics(a.data || null);
    } catch (err) {
      if (err?.response?.status === 401) { logout(); toast.error("Session expired"); }
      else toast.error("Failed to load data");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (authed) fetchAll(); }, [authed]); // eslint-disable-line

  const exportCsv = (data, filename) => {
    if (!data.length) return;
    const keys = Object.keys(data[0]);
    const rows = [keys.join(",")].concat(
      data.map(row => keys.map(k => JSON.stringify(row[k] ?? "")).join(","))
    );
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  if (!authed) {
    return (
      <div data-testid="admin-login-page" className="min-h-screen bg-matte flex items-center justify-center px-6">
        <form data-testid="admin-login-form" onSubmit={login} className="w-full max-w-md">
          <div className="text-[11px] uppercase tracking-luxe text-gold mb-4">Crowned Essence</div>
          <h1 className="font-serif text-4xl text-ivory">Curator Access</h1>
          <p className="mt-3 text-ivory/60 text-sm">Private area — team only.</p>
          <div className="mt-8">
            <label className="block text-[10px] uppercase tracking-luxe-sm text-ivory/50 mb-2">Password</label>
            <input
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-transparent border-b border-ivory/30 py-3 outline-none focus:border-gold text-ivory"
              required
            />
          </div>
          <button
            data-testid="admin-login-submit"
            type="submit"
            disabled={loading}
            className="mt-8 px-10 py-4 bg-gold text-matte text-[11px] uppercase tracking-luxe-sm hover:bg-gold-soft disabled:opacity-50"
          >
            {loading ? "Verifying…" : "Enter"}
          </button>
        </form>
      </div>
    );
  }

  const rows =
    tab === "waitlist" ? waitlist :
    tab === "partners" ? partners :
    contacts;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-ivory pt-24 pb-16">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <div className="text-[11px] uppercase tracking-luxe text-gold">Curator Dashboard</div>
            <h1 className="font-serif text-4xl mt-1">Crowned Essence Admin</h1>
          </div>
          <div className="flex gap-3">
            <button data-testid="admin-refresh" onClick={fetchAll} className="px-6 py-3 border border-matte text-[11px] uppercase tracking-luxe-sm">Refresh</button>
            <button data-testid="admin-export" onClick={() => exportCsv(rows, `${tab}.csv`)} className="px-6 py-3 bg-matte text-gold text-[11px] uppercase tracking-luxe-sm">Export CSV</button>
            <button data-testid="admin-logout" onClick={logout} className="px-6 py-3 border border-taupe text-[11px] uppercase tracking-luxe-sm">Sign Out</button>
          </div>
        </div>

        <div className="flex gap-2 border-b border-taupe/50 mb-8">
          {[
            { k: "waitlist", label: `Waitlist (${waitlist.length})` },
            { k: "partners", label: `Partners (${partners.length})` },
            { k: "contacts", label: `Contacts (${contacts.length})` },
          ].map(t => (
            <button
              key={t.k}
              data-testid={`admin-tab-${t.k}`}
              onClick={() => setTab(t.k)}
              className={`px-6 py-3 text-[11px] uppercase tracking-luxe-sm border-b-2 -mb-px ${tab === t.k ? "border-gold text-matte" : "border-transparent text-matte/50 hover:text-matte"}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {analytics && <AnalyticsPanel data={analytics} activeTab={tab} />}

        {loading ? (
          <div className="py-24 text-center text-matte/50">Loading…</div>
        ) : rows.length === 0 ? (
          <div data-testid="admin-empty" className="py-24 text-center text-matte/50 font-serif text-2xl italic">No entries yet.</div>
        ) : (
          <div data-testid={`admin-table-${tab}`} className="overflow-x-auto border border-taupe/40">
            <table className="w-full text-sm">
              <thead className="bg-cream text-left">
                <tr>
                  {Object.keys(rows[0]).map(k => (
                    <th key={k} className="px-4 py-3 text-[10px] uppercase tracking-luxe-sm text-matte/60">{k.replace(/_/g,' ')}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id || i} className="border-t border-taupe/30 hover:bg-cream/50">
                    {Object.keys(rows[0]).map(k => (
                      <td key={k} className="px-4 py-3 align-top max-w-xs truncate">{
                        Array.isArray(r[k]) ? r[k].join(", ") : String(r[k] ?? "")
                      }</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}


function AnalyticsPanel({ data, activeTab }) {
  const { totals, by_source } = data;
  const which = activeTab === "partners" ? "partners" : "waitlist";
  const rows = by_source?.[which] || [];
  const grandTotal = totals?.[which] || 0;

  const sourceLabel = (s) => {
    if (!s || s === "direct") return "Direct / Untagged";
    // "instagram / bio / launch" -> capitalize
    return s.split(" / ").map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(" · ");
  };

  return (
    <div data-testid="admin-analytics" className="mb-10">
      <div className="grid grid-cols-3 gap-4 mb-6">
        <StatCard label="Waitlist" value={totals.waitlist} testId="stat-waitlist" />
        <StatCard label="Partners" value={totals.partners} testId="stat-partners" />
        <StatCard label="Contacts" value={totals.contacts} testId="stat-contacts" />
      </div>

      <div className="border border-taupe/40 bg-cream/40 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-luxe-sm text-gold">Traffic Sources</div>
            <div className="font-serif text-2xl mt-1">
              {which === "partners" ? "Partner applications" : "Waitlist signups"} by source
            </div>
          </div>
          <div className="text-xs text-matte/50 italic">Powered by UTM tags</div>
        </div>

        {rows.length === 0 ? (
          <div className="text-matte/50 italic text-sm py-6">No entries yet.</div>
        ) : (
          <div className="space-y-3">
            {rows.map((r) => {
              const pct = grandTotal ? Math.round((r.count / grandTotal) * 100) : 0;
              return (
                <div key={r.source} data-testid={`source-row-${r.source.replace(/[^a-z0-9]+/gi,'-').toLowerCase()}`}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-matte">{sourceLabel(r.source)}</span>
                    <span className="text-matte/60 font-sans tabular-nums">
                      <span className="text-matte font-medium">{r.count}</span>
                      <span className="mx-2 text-matte/30">·</span>
                      <span>{pct}%</span>
                    </span>
                  </div>
                  <div className="h-1.5 bg-taupe/30">
                    <div className="h-full bg-gold" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, testId }) {
  return (
    <div data-testid={testId} className="border border-taupe/40 bg-ivory p-6">
      <div className="text-[10px] uppercase tracking-luxe-sm text-gold">{label}</div>
      <div className="font-serif text-4xl mt-2 tabular-nums">{value}</div>
    </div>
  );
}
